import React, { useEffect, useId, useState } from 'react';
import clsx from 'clsx';
import IconDragDropVertical from '@/assets/icons/icon-drag-drop-vertical.svg?react';
import IconMoreVertical from '@/assets/icons/icon-more-vertical.svg?react';
import { IconSimpleButton } from '@/components/buttons/IconSimpleButton/IconSimpleButton';
import { SelectItem } from '@/components/ui/select';
import { SelectField } from '@/components/dropdowns/SelectField/SelectField';
import { Input } from '@/components/forms/Input/Input';
import { CheckIcon } from 'lucide-react';

export interface OptionFieldProps {
  id?: string;
  className?: string;
  text?: string;
  selected?: boolean;
  draggable?: boolean;
  onEditText?: (id: string, nextText: string) => void;
  onDragStart?: (id?: string) => void;
  onDragEnd?: (id?: string) => void;
  onDropOver?: (sourceId?: string, targetId?: string, placement?: 'before' | 'after') => void;
  onDragOverItem?: (targetId?: string, placement?: 'before' | 'after') => void;
  onDragLeaveItem?: (targetId?: string) => void;
  // Menu actions
  isDefault?: boolean;
  onSetDefault?: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
  disableDelete?: boolean;
}

export const OptionField: React.FC<OptionFieldProps> = ({
  id,
  className = '',
  text = 'Option 1',
  selected = false,
  draggable = true,
  onEditText,
  onDragStart,
  onDragEnd,
  onDropOver,
  onDragOverItem,
  onDragLeaveItem,
  isDefault = false,
  onSetDefault,
  onDuplicate,
  onDelete,
  disableDelete = false,
}) => {
  const autoId = useId();
  const optionId = id ?? `option-${autoId}`;
  const [dragging, setDragging] = useState(false);
  const [menuKey, setMenuKey] = useState(0);
  const [canDrag, setCanDrag] = useState(false);
  const [draftText, setDraftText] = useState(text);

  useEffect(() => setDraftText(text), [text]);
  const commitEdit = () => {
    const trimmed = draftText.trim();
    onEditText?.(optionId, trimmed.length === 0 ? 'Option' : trimmed);
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    if (!draggable) return;
    if (!canDrag) {
      e.preventDefault();
      return;
    }
    setDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData('text/plain', optionId);
      // Use the element itself as drag image for a nicer ghost
      e.dataTransfer.setDragImage(e.currentTarget, 16, 16);
    } catch {
      // ignore drag image errors in unsupported environments
    }
    onDragStart?.(optionId);
  };

  const handleDragEnd = () => {
    setDragging(false);
    setCanDrag(false);
    onDragEnd?.(optionId);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const placement: 'before' | 'after' =
      e.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
    onDragOverItem?.(optionId, placement);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const placement: 'before' | 'after' =
      e.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
    onDragOverItem?.(optionId, placement);
  };

  const handleDragLeave = () => {
    onDragLeaveItem?.(optionId);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const sourceId = e.dataTransfer.getData('text/plain');
    if (sourceId && sourceId !== optionId) {
      const rect = e.currentTarget.getBoundingClientRect();
      const placement: 'before' | 'after' =
        e.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
      onDropOver?.(sourceId, optionId, placement);
    }
  };

  const baseClasses =
    'inline-flex h-9 w-full select-none items-center justify-start gap-2 rounded-md transition-all duration-150 ease-out will-change-transform';
  const stateClasses =
    selected || dragging
      ? 'bg-white shadow-[0px_0px_8px_0px_rgba(0,0,0,0.10)] scale-[1.01] cursor-grabbing'
      : '';

  return (
    <div
      id={optionId}
      className={clsx(baseClasses, stateClasses, className)}
      draggable={draggable}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      aria-grabbed={dragging}
    >
      <div
        className="option-drag-handle grid h-5 w-5 cursor-grab place-items-center"
        onMouseDown={() => setCanDrag(true)}
        onMouseUp={() => setCanDrag(false)}
        onMouseLeave={() => setCanDrag(false)}
        data-drag-handle
      >
        <IconDragDropVertical className="h-4 w-4 text-neutral-500" />
      </div>

      <div className={clsx('flex h-8 flex-1')}>
        <Input
          value={draftText}
          onChange={(e) => {
            const val = (e.target as HTMLInputElement).value;
            setDraftText(val);
            onEditText?.(optionId, val);
          }}
          onBlur={commitEdit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitEdit();
            }
          }}
          containerClassName="w-full"
          className="h-8 p-2"
          placeholder="Option"
        />
      </div>

      <SelectField
        key={menuKey}
        text=""
        size="none"
        placeholder=""
        className="-mr-1"
        classNameTrigger="!p-0 !bg-transparent !h-6 !w-6"
        classNameValue="sr-only"
        childrenTrigger={<IconSimpleButton icon={IconMoreVertical} size={24} noBgWhenDefault />}
        onValueChange={(val) => {
          if (val === 'default') onSetDefault?.();
          if (val === 'duplicate') onDuplicate?.();
          if (val === 'delete' && !disableDelete) onDelete?.();
          setMenuKey((k) => k + 1);
        }}
        side="bottom"
        avoidCollisions
      >
        <SelectItem
          className="w-full pr-2 [&>span:last-child]:!flex [&>span:last-child]:w-full"
          value="default"
        >
          <div className="flex w-full items-center justify-between gap-2">
            <span className={clsx('flex', isDefault && 'text-indigo-500')}>Default</span>
            {isDefault && <CheckIcon className="h-4 min-h-4 w-4 min-w-4 text-indigo-500" />}
          </div>
        </SelectItem>
        <SelectItem value="duplicate">Duplicate</SelectItem>
        <SelectItem value="delete" disabled={disableDelete}>
          Delete
        </SelectItem>
      </SelectField>
    </div>
  );
};

export default OptionField;
